import { type ComponentProps, forwardRef, type ReactNode, useEffect } from 'react'
import Image from 'next/image'
import clsx from 'clsx'
import { motion, type Variants } from 'motion/react'
import { HeadingBlock } from '@/blocks/HeadingBlock/HeadingBlock'
import { Video, type VideoProps, type VideoRef } from '@/components/Video/Video'
import { filterByBlockType } from '@/services/storyblok/Storyblok.helpers'
import { assignInlineVars } from '@vanilla-extract/dynamic'
import { Badge } from 'ui/src/components/Badge/Badge'
import { Heading } from 'ui/src/components/Heading/Heading'
import { Text } from 'ui/src/components/Text/Text'
import { sprinkles } from 'ui/src/theme/sprinkles.css'
import { framerTransitions } from 'ui/src/theme/transitions'
import type { ImageItem, Item, StoryAspectRatio, StoryVariant, TextItem, VideoItem } from './StoryCarousel.types'
import {
  aspectRatioVar,
  story,
  storyTextOnly,
  storyVariants,
  tagsContainer,
  textStoryHeadingWrapper,
} from './Story.css'
import { ITEM_OFFSET_Y } from './StoryCarouselDesktop.css'

// Animation lives on StoryWrapper itself (not a parent) so backdrop-filter: blur works correctly.
// A parent with opacity < 1 or a non-identity transform creates a compositing surface that
// prevents backdrop-filter from sampling real page content behind the card, causing janky animation
export const storyItemVariants: Variants = {
  hidden: { opacity: 0, y: ITEM_OFFSET_Y },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: framerTransitions.defaultDuration,
      ...framerTransitions.easeInOutCubic,
    },
  },
}

type StoryWrapperProps = {
  aspectRatio?: StoryAspectRatio
  storyVariant?: StoryVariant
  className?: string
  children: ReactNode
  shouldFadeIn?: boolean
}

function StoryWrapper({
  aspectRatio = '3 / 4',
  storyVariant = 'grey',
  className = story,
  children,
  shouldFadeIn,
}: StoryWrapperProps) {
  return (
    <motion.div
      className={clsx(className, storyVariants[storyVariant])}
      style={assignInlineVars({ [aspectRatioVar]: aspectRatio })}
      variants={shouldFadeIn ? storyItemVariants : undefined}
    >
      {children}
    </motion.div>
  )
}

export type VideoStoryProps = {
  item: VideoItem
  aspectRatio?: StoryAspectRatio
  storyVariant?: StoryVariant
  shouldFadeIn?: boolean
} & Omit<VideoProps, 'sources' | 'loop' | 'showControls' | 'muted' | 'resetOnEnd' | 'poster'>

export const VideoStory = forwardRef<VideoRef, VideoStoryProps>(
  ({ item, aspectRatio, storyVariant, shouldFadeIn, ...delegated }, ref) => {
    return (
      <StoryWrapper aspectRatio={aspectRatio} storyVariant={storyVariant} shouldFadeIn={shouldFadeIn}>
        <StoryTags tags={item.tags} />
        <Video
          ref={ref}
          sources={item.asset.sources}
          loop={item.asset.loop}
          showControls={false}
          muted={true}
          resetOnEnd={false}
          poster={item.asset.poster}
          {...delegated}
        />
        <StoryDescription title={item.title} subtitle={item.subtitle} />
      </StoryWrapper>
    )
  },
)
VideoStory.displayName = 'VideoStory'

export type ImageStoryProps = {
  item: ImageItem
  aspectRatio?: StoryAspectRatio
  storyVariant?: StoryVariant
  shouldFadeIn?: boolean
} & Omit<ComponentProps<typeof Image>, 'src' | 'alt'>

export function ImageStory({ item, aspectRatio, storyVariant, shouldFadeIn, ...delegated }: ImageStoryProps) {
  return (
    <StoryWrapper aspectRatio={aspectRatio} storyVariant={storyVariant} shouldFadeIn={shouldFadeIn}>
      <StoryTags tags={item.tags} />
      {/* Next's image requires a positioned container for 'fill' to work properly */}
      <div className={sprinkles({ position: 'relative', flexGrow: 1 })}>
        <Image src={item.asset.src} alt={item.asset.alt} fill={true} objectFit="cover" {...delegated} />
      </div>
      <StoryDescription title={item.title} subtitle={item.subtitle} />
    </StoryWrapper>
  )
}

export function TextStory({
  item,
  aspectRatio,
  storyVariant,
  shouldFadeIn,
  onReady,
}: {
  item: TextItem
  aspectRatio?: StoryAspectRatio
  storyVariant?: StoryVariant
  shouldFadeIn?: boolean
  onReady?: () => void
}) {
  useEffect(() => {
    onReady?.()
  }, [onReady])

  const headingBlocks = filterByBlockType(item.heading, HeadingBlock.blockName)
  const headingBlock = headingBlocks[0]

  return (
    <StoryWrapper
      aspectRatio={aspectRatio}
      storyVariant={storyVariant}
      shouldFadeIn={shouldFadeIn}
      className={storyTextOnly}
    >
      <StoryTags tags={item.tags} />
      <div className={textStoryHeadingWrapper}>
        {headingBlock && <HeadingBlock blok={headingBlock} nested={true} />}
      </div>
      {item.subtitle && (
        <Text as="p" color="textSecondaryOnGray" align="center" balance={true} size={{ _: 'sm', md: 'md' }}>
          {item.subtitle}
        </Text>
      )}
    </StoryWrapper>
  )
}

function StoryTags({ tags }: { tags: Item['tags'] }) {
  if (!tags || tags.length === 0) return null

  return (
    <div className={tagsContainer}>
      {tags.map((tag) => (
        <Badge key={tag} variant="white" size="small">
          {tag}
        </Badge>
      ))}
    </div>
  )
}

function StoryDescription({ title, subtitle }: Pick<VideoItem | ImageItem, 'title' | 'subtitle'>) {
  if (!title && !subtitle) return null

  return (
    <div>
      {title && (
        <Heading as="h3" color="textPrimary" variant="standard.18" align="center">
          {title}
        </Heading>
      )}
      {subtitle && (
        <Text as="p" color="textSecondary" align="center" balance={true}>
          {subtitle}
        </Text>
      )}
    </div>
  )
}
